import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { User, Mail, Phone } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import SuccessModal from '../components/modals/SuccessModal';
import { useNotificationModal } from '../hooks/useNotificationModal';
import CountryCodeSelect from '../components/CountryCodeSelect';

import '../styles/ProfileSettings.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ProfileSettings() {
    const { userData, login } = useContext(AuthContext);

    const {
        showSuccessModal,
        successMessage,
        successTitle,
        showErrorModal,
        errorMessage,
        errorTitle,
        showSuccess,
        showError,
        hideSuccess,
        hideError
    } = useNotificationModal();

    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phone: '',
        countryCode: '+94'
    });
    const [loading, setLoading] = useState(false);
    const company_code = userData?.company_code;

    useEffect(() => {
        const fetchAdmin = async () => {
            if (!userData?.id) return;
            try {
                const token = localStorage.getItem('authToken');
                const res = await axios.get(
                    `${BASE_URL}/api/admin/auth/get-admin/${userData.id}`,
                    {
                        headers: { Authorization: token ? `Bearer ${token}` : '' }
                    }
                );

                const admin = res.data?.admin || res.data;
                if (admin) {
                    setProfileData({
                        name: admin.name || '',
                        email: admin.email || '',
                        phone: admin.phone_number || '',
                        countryCode: admin.country_code || '+94'
                    });
                    // Update context/localStorage userData if differs
                    const currentStored = JSON.parse(localStorage.getItem('userData') || 'null');
                    if (currentStored && (currentStored.name !== admin.name || currentStored.email !== admin.email || currentStored.phone_number !== admin.phone_number)) {
                        const merged = { ...currentStored, ...admin };
                        localStorage.setItem('userData', JSON.stringify(merged));
                        const currentToken = localStorage.getItem('authToken');
                        if (currentToken) login(currentToken, merged);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch admin data', err);
            }
        };

        fetchAdmin();
    }, [userData, login]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // basic validation
        if (!profileData.name || !profileData.email) {
            showError('Name and email are required.', 'Validation Error');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const id = userData?.id;

            if (!id) throw new Error('User id not found');

            const cleanPhone = profileData.phone.replace(/\D/g, '');
            
            const payload = {
                name: profileData.name,
                email: profileData.email,
                country_code: profileData.countryCode,
                phone_number: cleanPhone,
                company_code: company_code
            };
            delete payload.password;

            const res = await axios.put(
                `${BASE_URL}/api/admin/auth/update-admin-profile/${id}`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token ? `Bearer ${token}` : ''
                    }
                }
            );

            const updated = res.data?.admin || res.data;
            if (updated) {
                const newUser = { ...userData, ...updated };
                localStorage.setItem('userData', JSON.stringify(newUser));
                const currentToken = localStorage.getItem('authToken');
                if (currentToken) login(currentToken, newUser);
            }

            showSuccess('Profile updated successfully.', 'Success');
        } catch (err) {
            console.error('Profile update error', err);
            const message = err?.response?.data?.message || err.message || 'Failed to update profile';
            showError(message, 'Error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="content-section">
            <div className='pro-settings-container'>
                <div className="style-header">
                    <h1>  <User className="me-2" /> Profile Information </h1>
                </div>
                
                <Form className="info-card" onSubmit={handleSubmit}>
                    <div className="form-content-wrapper">
                        {/* Left side - Profile fields */}
                        <div className="profile-fields">
                            <div className="form-group-custom">
                                <label className="form-label fw-bold">Name</label>
                                <Form.Control
                                    type="text"
                                    className="form-control-custom"
                                    value={profileData.name}
                                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            
                            <div className="form-group-custom">
                                <label className="form-label fw-bold">
                                    <Mail size={16} className="me-1" />
                                    Email Address
                                </label>
                                <Form.Control
                                    type="email"
                                    className="form-control-custom"
                                    value={profileData.email}
                                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                                />
                            </div>
                            
                            <div className="form-group-custom">
                                <label className="form-label fw-bold">
                                    <Phone size={16} className="me-1" />
                                    Phone Number
                                </label>
                                <div className="d-flex gap-2">
                                    <CountryCodeSelect
                                        value={profileData.countryCode}
                                        onChange={(code) =>
                                            setProfileData((prev) => ({ ...prev, countryCode: code }))
                                        }
                                        placeholder="Code"
                                        isRequired={true}
                                        width="100px"
                                    />
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={profileData.phone}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setProfileData(prev => ({ ...prev, phone: value }));
                                        }}
                                        placeholder="Enter phone number"
                                        className="form-control-custom"
                                        style={{ flex: 1 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="save-button-container">
                        <Button type="submit" disabled={loading} variant="primary">
                            {loading ? (
                                <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...</>
                            ) : 'Save Changes'}
                        </Button>
                    </div>
                </Form>
            </div>

            {/* Success Modal */}
            <SuccessModal
                show={showSuccessModal}
                onHide={hideSuccess}
                message={successMessage}
                title={successTitle}
                variant="success"
            />

            {/* Error Modal */}
            <SuccessModal
                show={showErrorModal}
                onHide={hideError}
                message={errorMessage}
                title={errorTitle}
                variant="error"
            />
        </div>  
    );
}
